/**
 * 图片 AI 多模态问答 — 对 image block 问 AI
 * 对齐 features.md 1.20-1.22
 *
 * 用 GPT-4o 等多模态模型识别图片内容
 * 1.20：真正把图片（data URL）传给 vision 模型
 */

import { useState } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';
import { IconButton } from '@hump/ui';
import { isConfigured } from '../../lib/ai/config';
import { chatCompletion } from '../../lib/ai/provider';

export function ImageAiButton({ imageData, caption }: { imageData: string; caption?: string }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const configured = isConfigured();

  async function askAboutImage(question: string) {
    if (!configured) return;
    setLoading(true);
    setResult('');
    try {
      // 1.20 多模态：user content 改为数组格式（text + image_url）
      // OpenAI vision API 格式，DeepSeek/GLM 等 OpenAI 兼容接口同样支持
      const prompt =
        question || '请描述这张图片的内容，包括图表类型、主要发现和数据趋势。';
      const response = await chatCompletion([
        {
          role: 'system',
          content: `你是一个阅读助手。用户正在阅读一篇文档，遇到了一张图片${
            caption ? `（标题：${caption}）` : ''
          }。请基于图片实际内容进行分析，识别图表类型、关键信息和数据趋势。`,
        },
        {
          role: 'user',
          content: [
            { type: 'text', text: prompt },
            { type: 'image_url', image_url: { url: imageData } },
          ],
        },
      ]);
      setResult(response);
    } catch (e) {
      setResult('AI 分析失败：' + (e as Error).message);
    }
    setLoading(false);
  }

  if (!configured) return null;

  return (
    <>
      <IconButton
        size="sm"
        onClick={() => setOpen(true)}
        title="用 AI 分析图片"
        aria-label="用 AI 分析图片"
      >
        <Sparkles className="w-4 h-4 text-camel" />
      </IconButton>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-charcoal/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-white dark:bg-warm-dark rounded-xl shadow-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-medium text-charcoal dark:text-charcoal-light">
                📊 AI 图片分析
              </h3>
              <IconButton size="sm" onClick={() => setOpen(false)} aria-label="关闭">
                <X className="w-4 h-4" />
              </IconButton>
            </div>

            {/* 图片预览 */}
            <img
              src={imageData}
              alt={caption || ''}
              className="w-full max-h-48 object-contain rounded-lg mb-3"
            />

            {/* 快捷问题 */}
            <div className="flex flex-wrap gap-2 mb-3">
              {['这张图说明了什么？', '描述主要发现', '解释数据趋势'].map((q) => (
                <button
                  key={q}
                  onClick={() => askAboutImage(q)}
                  disabled={loading}
                  className="px-2.5 py-1 rounded-md text-xs bg-camel/10 text-camel-dark dark:text-camel hover:bg-camel/20 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>

            {/* 结果 */}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-charcoal/50">
                <Loader2 className="w-4 h-4 animate-spin" />
                AI 分析中...
              </div>
            )}
            {result && (
              <div className="p-3 rounded-md bg-warm-white dark:bg-border-warm-dark text-sm text-charcoal/80 dark:text-charcoal-light/80 max-h-48 overflow-y-auto">
                {result}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
